<template>
  <!--
    `append-to-body` 가 필요한 이유: 이 창은 **다른 다이얼로그 안**(메뉴·옵션 등록 폼)과 **팝오버 안**
    (엑셀 업로드·테이블 추가)에서도 열린다. 본문에 붙이지 않으면 Element 가 부모 레이어 안에 그려
    가려지거나 부모가 닫힐 때 같이 사라진다.
  -->
  <el-dialog
    class="store-picker-dialog"
    :title="text.dialogTitle"
    :visible="visible"
    append-to-body
    width="720px"
    @update:visible="$emit('update:visible', $event)"
    @opened="onOpened">
    <div class="store-picker__search">
      <el-input
        ref="search"
        v-model="query"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        :placeholder="text.searchPlaceholder"
        @input="onQueryInput"
        @keydown.native.down.prevent="moveBy(1)"
        @keydown.native.up.prevent="moveBy(-1)"
        @keydown.native.right.prevent="expandActive(true)"
        @keydown.native.left.prevent="expandActive(false)"
        @keydown.native.enter.prevent="onEnter" />
      <p v-if="queryTooShort" class="store-picker__hint">{{ text.searchTooShort }}</p>
    </div>

    <!-- 검색한 사업장으로 들어와 있으면 돌아갈 길을 남긴다 — 뒤로 갈 곳이 없으면 그 줄도 없다 -->
    <div v-if="inCustStores" class="store-picker__crumb">
      <el-button size="mini" type="text" icon="el-icon-arrow-left" @click="backToSearch">{{
        text.dialogTitle
      }}</el-button>
      <span class="store-picker__crumb-name">{{ custStoresName }}</span>
    </div>

    <div v-if="showRecent" class="store-picker__recent">
      <span class="store-picker__section">{{ text.recentTitle }}</span>
      <el-button
        v-for="row in recent"
        :key="row.key"
        size="mini"
        plain
        class="store-picker__recent-item"
        @click="pick(row)"
        >{{ row.storeNm }}</el-button
      >
    </div>

    <el-alert v-if="failed" :title="text.loadFailed" type="error" :closable="false" show-icon>
      <el-button size="mini" type="text" @click="retry">{{ text.retryAction }}</el-button>
    </el-alert>

    <!--
      목록이 **트리**라고 말한다 — 화살표·Space·Enter 가 검색 칸을 떠나서도 살아 있어야 하고
      (행을 한 번 클릭하면 포커스가 입력에서 떠난다), 스크린리더에는 div 더미가 아니라 트리로 읽혀야 한다.
      활성 행은 aria-activedescendant 로 가리킨다(roving focus) — 줄마다 포커스를 옮기지 않아
      50줄·100줄에서도 포커스 이동 비용이 없다.
    -->
    <div
      v-else
      ref="rows"
      class="store-picker__rows"
      role="tree"
      tabindex="0"
      :aria-multiselectable="multiple ? 'true' : 'false'"
      :aria-activedescendant="activeRowId"
      v-loading="busy"
      @keydown.down.prevent="moveBy(1)"
      @keydown.up.prevent="moveBy(-1)"
      @keydown.right.prevent="expandActive(true)"
      @keydown.left.prevent="expandActive(false)"
      @keydown.space="onSpace"
      @keydown.enter.prevent="onEnter">
      <!--
        트리든 검색 결과든 **같은 평면 배열**이다(`visibleRows`) — 그래서 그리는 갈래도, 키보드 이동
        규칙도 한 벌이다. 「읽는 중」·「더 보기」·「비어 있음」이 분기가 아니라 줄인 것도 같은 이유다.
      -->
      <div
        v-for="(row, index) in rows"
        :key="row.key"
        :id="rowDomId(row)"
        class="store-picker__row"
        :class="rowClass(row, index)"
        :style="{ paddingLeft: 8 + row.depth * 16 + 'px' }"
        role="treeitem"
        :aria-level="row.depth + 1"
        :aria-expanded="ariaExpanded(row)"
        :aria-checked="ariaChecked(row)"
        :aria-selected="ariaSelected(row)"
        @click="onRowClick(row, index)">
        <template v-if="row.type === NODE.CUST">
          <i class="store-picker__caret" :class="caretClass(row)" @click.stop="toggle(row)"></i>
          <el-checkbox
            v-if="multiple"
            :value="checkState(row) === SELECT.ON"
            :indeterminate="checkState(row) === SELECT.PARTIAL"
            @change="check(row, $event)"
            @click.native.stop />
          <span class="store-picker__name">{{ row.custNm }}</span>
          <el-tag v-if="shopGroup(row)" size="mini" type="warning">{{ text.shopGroupBadge }}</el-tag>
          <el-tag v-if="inactive(row)" size="mini" type="info">{{ text.inactiveBadge }}</el-tag>
          <span class="store-picker__sub">{{ countText(row) }}</span>
          <span v-if="row.path && row.path.length" class="store-picker__sub">{{ pathLine(row) }}</span>
          <el-button v-if="row.path" size="mini" type="text" @click.stop="openCustStores(row)">{{
            text.storeCountLabel
          }}</el-button>
        </template>

        <!-- 「하나만 고르기」를 숨은 모드로 두지 않는다 — 보이는 줄로 만든다(§1-6) -->
        <template v-else-if="row.type === NODE.DIRECT">
          <i class="store-picker__caret store-picker__caret--none"></i>
          <!--
            묶음도 **3상태**다 — 직접 매장 하나를 뺀 묶음은 ON 이 아니다. 두 상태로 두면 「부분을
            전체로」 바꾸려는 클릭이 ON 으로 읽혀 전체 해제가 된다
          -->
          <el-checkbox
            v-if="multiple"
            :value="checkState(row) === SELECT.ON"
            :indeterminate="checkState(row) === SELECT.PARTIAL"
            @change="check(row, $event)"
            @click.native.stop />
          <span class="store-picker__name store-picker__direct">{{ text.directGroupLabel }}</span>
          <span class="store-picker__sub">{{ row.count }}{{ text.storeCountUnit }}</span>
        </template>

        <template v-else-if="row.type === NODE.STORE">
          <el-checkbox
            v-if="multiple"
            :value="checkState(row) === SELECT.ON"
            @change="check(row, $event)"
            @click.native.stop />
          <span class="store-picker__name">{{ row.storeNm }}</span>
          <span class="store-picker__sub">{{ subText(row) }}</span>
          <span v-if="row.path && row.path.length" class="store-picker__sub">{{ pathLine(row) }}</span>
        </template>

        <span v-else-if="row.type === NODE.MORE" class="store-picker__more">{{ text.moreAction }}</span>
        <span v-else class="store-picker__sub">{{ row.type === NODE.LOADING ? text.searching : text.treeEmpty }}</span>
      </div>

      <p v-if="rows.length === 0 && !busy" class="store-picker__empty">
        {{ searching ? text.searchEmpty : text.treeEmpty }}
      </p>
    </div>

    <!--
      선택 요약 — 사람이 만든 **규칙이 그대로 칩**이다. 나열로 풀 수 없는 선택(안 펼친 하부 전체)을
      말할 수 있는 유일한 모양이고, × 로 그 규칙만 걷는다.
    -->
    <div v-if="multiple" class="store-picker__summary">
      <span class="store-picker__section">{{ summaryText }}</span>
      <el-tag
        v-for="chip in chips"
        :key="chip.key"
        size="mini"
        closable
        :type="chip.exclude ? 'info' : 'success'"
        @close="removeChip(chip)"
        >{{ chipLabel(chip) }}</el-tag
      >
      <el-button v-if="chips.length > 0" size="mini" type="text" @click="clearRules">{{ text.clearAction }}</el-button>
      <p v-if="overLimit" class="store-picker__hint">{{ text.tooManyRules }}</p>
    </div>

    <span slot="footer">
      <span class="store-picker__keys">{{ multiple ? text.keyboardHintMultiple : text.keyboardHint }}</span>
      <el-button size="small" @click="$emit('update:visible', false)">{{
        multiple ? text.cancelAction : text.closeAction
      }}</el-button>
      <!-- `confirm()` 으로 부른다 — `@click="confirm"` 이면 MouseEvent 가 출처 인자로 들어간다 -->
      <el-button v-if="multiple" size="small" type="primary" :loading="resolving" @click="confirm()">{{
        text.confirmAction
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  NODE_TYPE,
  RULE_TARGET,
  SCOPE_CUSTS_API,
  SCOPE_READ_KEY,
  SCOPE_RESOLVE_API,
  SCOPE_SEARCH_API,
  SCOPE_STORES_API,
  SEARCH_DEBOUNCE_MS,
  SEARCH_NODE_PREFIX,
  SELECTION,
  SILENT_READ_CONFIG,
  chainOf,
  copyRules,
  custNodesOf,
  custSelection,
  custsQuery,
  directSelection,
  emptyRules,
  hasCheckAxis,
  hasChildren,
  hasRules,
  isInactive,
  isPickable,
  isSearchable,
  isShopGroup,
  moveCursor,
  nextStorePage,
  pathText,
  resolveBody,
  resolveResultOf,
  rulesOverLimit,
  scopeUnavailable,
  searchNodeKey,
  searchQuery,
  searchResultOf,
  selectedCount,
  selectionChips,
  storeSelected,
  storeCountText,
  storeNamesOf,
  storePageOf,
  storePageReadKey,
  storeRowsOf,
  storeSubText,
  storesQuery,
  toggleExpanded,
  withoutKey,
  visibleRows,
  withRule,
  withoutRule,
} from '@/utils/scopeTree'
import { STORE_PICKER_TEXT } from '@/utils/scopeTreeText'
import { httpStatusOf } from '@/plugins/http/policy'
import { latestGuard, readGate } from '@/utils/latestGuard'

/**
 * 확정이 **어디서 시작했나** — 창을 닫는 것이 무엇을 취소하는지, 그리고 **무엇이 창을 닫을 수 있는지**가
 * 여기서 갈린다.
 *
 * `dialog` 는 사람이 창 안에서 누른 확정이다(닫으면 취소한 것이다). `default` 는 화면 진입의 기본
 * 선택([loadDefault])이라 **창과 무관하게 산다** — 그 두 왕복 동안 사람이 창을 열었다 닫았다고 해서
 * 아직 아무것도 고르지 않은 진입 확정을 버리면, 부모는 `confirm` 을 영영 못 받고 첫 조회가 기본 매장
 * 없이 나간다.
 *
 * **닫음의 주인도 이 한 문장이 정한다**: 창과 무관하게 사는 확정은 **창을 닫을 자격도 없다**.
 * 세 자리가 같은 규칙을 읽는다 — 닫힘 워처(무엇을 버리는가)·중복 가드([resolving], 무엇을 막는가)·
 * [commitConfirm](무엇이 창을 닫는가).
 */
const CONFIRM_ORIGIN = Object.freeze({ DIALOG: 'dialog', DEFAULT: 'default' })

/**
 * **매장 선택 창** — 검색 칸 + 범위 트리(후속 2 §1-6). 모드는 둘이고 **한 벌의 트리·검색·키보드**를 쓴다:
 *
 * - **단일**(매장 셀렉트 12곳): 매장 잎을 누르면 그대로 선택 완료.
 * - **다중**(통계 화면): 값이 매장 나열이 아니라 **선택 규칙**이다(§1-5). 지연 로딩 트리에서 아직 펼치지
 *   않은 하부를 한 번의 클릭으로 고르려면 나열이 아니라 규칙이어야 하고, 확정할 때 서버가 그 규칙을
 *   매장 집합으로 푼다(`POST /scope/tree/resolve`).
 *
 * 그리는 것 말고는 아무것도 판정하지 않는다: 무엇이 보이는지는 서버가 좁히고(§1-3), 어떤 줄이 서는지·
 * 무엇이 고를 수 있는지·규칙이 어떻게 접히는지는 순수 함수(`utils/scopeTree`)가, 문구는
 * `utils/scopeTreeText` 가 답한다. 여기 남는 것은 **조회(경계)와 사용자 입력**뿐이다.
 */
export default {
  name: 'StorePickerDialog',

  props: {
    visible: { type: Boolean, default: false },
    /** `false` 면 매장 하나, `true` 면 선택 규칙(통계 화면) */
    multiple: { type: Boolean, default: false },
    /** 최근 목록 — 단일 모드에서만 쓴다 */
    recent: { type: Array, default: () => [] },
  },

  data() {
    return {
      text: STORE_PICKER_TEXT,
      NODE: NODE_TYPE,
      SELECT: SELECTION,
      query: '',
      /** `{roots, children, expanded}` — [visibleRows] 가 읽는 모양 그대로다 */
      tree: { roots: [], children: {}, expanded: {} },
      /** 규칙이 읽는 계보·이름·매장 수 — 불러온 노드로 조금씩 채운다 */
      index: { custParents: {}, storeCust: {}, counts: {}, names: {}, storeNames: {} },
      /**
       * **편집본** — 창 안에서 사람이 고치는 규칙이다. 「취소」·Esc·바깥 클릭은 이것을 버린다
       * (다음 열기에서 [committedRules] 로 다시 채워진다).
       */
      rules: emptyRules(),
      /**
       * **확정본** — 부모가 들고 있는 값과 짝이다. 갱신하는 자리는 둘뿐이다: 최신 확정의 성공과
       * 화면 진입의 기본 선택([loadDefault]). 나눠 두지 않으면 취소한 편집이 다음 창에 그대로 뜨고
       * 다음 확정에 섞인다 — 부모의 값과 창이 보여 주는 것이 갈린다.
       */
      committedRules: emptyRules(),
      searchRows: [],
      /** 검색한 사업장의 매장 — `{node, loaded}`. `loaded` 는 `tree.children[key]` 와 **같은 모양**이다 */
      custStores: null,
      searching: false,
      busy: false,
      /**
       * 진행 중인 확정 — `{seq, origin}` 이고 없으면 `null`. 「진행 중인가」를 따로 든 불리언으로
       * 두었더니 창을 닫아 순번을 무효로 만들어도 옛 요청의 `finally` 가 올 때까지 참으로 남아,
       * 다시 연 창의 확정이 조용히 무시되고 버튼만 돌았다. 상태를 둘 두지 않고 [resolving] 을 여기서
       * 파생시킨다. 출처를 함께 드는 이유는 [CONFIRM_ORIGIN] 에 있다.
       */
      confirmInFlight: null,
      failed: false,
      loaded: false,
      activeIndex: -1,
      debounce: null,
      /** 늦게 온 검색 응답이 최신 질의어의 결과를 덮지 못하게 */
      guard: latestGuard(),
      /**
       * 확정 축의 순번 — **요청 당시의 선택만** 부모에게 간다. 창을 닫으면 무효가 된다(늦게 온
       * `resolve` 가 이미 취소한 선택으로 부모의 조회를 바꾸지 않게)
       */
      confirmGuard: latestGuard(),
      /** 나가 있는 읽기 전부 — 중복 요청·버림·바쁨이 여기 한 곳에서 나온다([readGate]) */
      gate: readGate(),
    }
  },

  computed: {
    rows() {
      // 검색 상세도 트리와 **같은 조립기**를 쓴다 — 묶음 행·매장·「더 보기」가 한 함수에서 나온다
      if (this.custStores) return storeRowsOf(this.custStores.node, this.custStores.loaded, 0)
      if (this.searching) return this.searchRows

      return visibleRows(this.tree)
    },
    /** 활성 행의 DOM id — aria-activedescendant 가 가리킬 자리(없으면 빈 문자열) */
    activeRowId() {
      const row = this.rows[this.activeIndex]

      return row ? this.rowDomId(row) : ''
    },
    queryTooShort() {
      return this.query.trim().length > 0 && !isSearchable(this.query)
    },
    showRecent() {
      return !this.multiple && !this.searching && !this.custStores && this.recent.length > 0
    },
    inCustStores() {
      return !!this.custStores
    },
    custStoresName() {
      return this.custStores ? this.custStores.node.custNm : ''
    },
    chips() {
      return this.multiple ? selectionChips(this.rules, this.index) : []
    },
    localCount() {
      return selectedCount(this.rules, this.index)
    },
    summaryText() {
      if (!hasRules(this.rules)) return this.text.summaryEmpty
      const count = this.localCount

      return count == null ? this.text.summaryUnknown : `${this.text.summaryPrefix} ${count}${this.text.storeCountUnit}`
    },
    overLimit() {
      return this.multiple && rulesOverLimit(this.rules)
    },
    /**
     * **이 창이** 확정 중인가 — 진행 중 표시의 주인은 [confirmInFlight] **하나**다.
     *
     * 출처를 가리는 이유: 화면 진입의 기본 확정은 창이 닫힌 채 도는 **남의 일**이다. 그것까지 여기로
     * 세면 확정 버튼이 `:loading`(= `disabled`)으로 서서, 창을 열어 고른 사람이 **확정을 누를 수조차
     * 없다** — 기본 확정이 응답할 때까지. 그 사이의 클릭을 삼키지 않겠다는 것이 이 창의 계약이므로
     * 표시도 같은 축을 봐야 한다.
     *
     * 한때 `confirmGuard.isLatest(...)` 를 함께 읽었는데, 그 가드는 번호를 클로저에 들고 객체를 얼려
     * 돌려주므로(Vue 2 의 `observe()` 가 확장 불가 객체를 건너뛴다) **추적되지 않는 의존**이었다.
     * 내려놓는 자리는 둘뿐이다: 자기 순번의 `finally` 와 창이 닫히는 전이.
     */
    resolving() {
      return this.confirmInFlight != null && this.confirmInFlight.origin === CONFIRM_ORIGIN.DIALOG
    },
  },

  watch: {
    /**
     * **여는 순간이 편집본이 태어나는 순간이고, 닫는 순간이 나가 있는 확정을 버리는 순간이다.**
     *
     * 열 때는 확정본의 **사본**을 편집본으로 삼는다(같은 배열을 쓰면 취소가 뜻을 잃는다).
     *
     * 닫을 때 버리는 것은 **창 안에서 시작한 확정**뿐이다([CONFIRM_ORIGIN]) — 화면 진입의 기본 확정은
     * 창과 무관하게 살아야 한다. 성공한 확정은 부모에게 emit 한 **뒤에** 닫히므로 이 무효화에 걸리지
     * 않고, Esc·취소로 닫힌 뒤 도착한 응답만 버려진다.
     *
     * 두 줄인 이유는 **상태가 둘이기 때문**이다 — 「이 응답을 받아도 되는가」는 순번이, 「진행 중인가」는
     * [confirmInFlight] 가 답한다. 옛 요청의 `finally` 는 **자기 순번일 때만** 내려놓으므로 새 확정의
     * 표시를 풀지 않는다.
     */
    visible(next, prev) {
      if (next && !prev) this.rules = copyRules(this.committedRules)
      if (!next && prev && this.confirmInFlight && this.confirmInFlight.origin === CONFIRM_ORIGIN.DIALOG) {
        this.confirmGuard.invalidate()
        this.confirmInFlight = null
      }
    },
  },

  /**
   * 다중 모드는 **창을 열기 전에** 뿌리를 읽는다 — 오늘 통계 화면이 트리를 받자마자 첫 노드를 체크하고
   * 조회하는 것과 같은 기본을 지키려면, 화면 진입 시점에 규칙 하나(첫 뿌리 전체)가 서 있어야 한다.
   * 단일 모드는 그러지 않는다(열두 자리에서 화면마다 요청이 늘어난다).
   */
  mounted() {
    if (this.multiple) this.loadDefault()
  },

  beforeDestroy() {
    this.clearDebounce()
    this.guard.close()
    this.confirmGuard.close()
    // 나가 있는 읽기의 결과가 사라진 창을 건드리지 않게 — 바쁨도 여기서 끝난다
    this.gate.cancel()
  },

  methods: {
    /** 창이 열린 **뒤에** 읽는다 — 닫혀 있는 창이 조회를 시작하면 화면 진입마다 요청이 는다 */
    onOpened() {
      this.activeIndex = -1
      if (this.$refs.search) this.$refs.search.focus()
      if (!this.loaded) this.loadRoots()
    },

    /**
     * 첫 진입 기본 선택 — 뿌리를 읽고 **첫 뿌리 전체**를 골라 부모가 곧바로 조회하게 한다.
     *
     * **호출자가 하나뿐이라**(`mounted` 의 다중 모드 갈래) 같은 읽기가 이미 나가 있어 문에 막히는
     * 경우가 없다. 재시도·매장 전환·창 재사용으로 이 메서드를 다시 부르는 자리가 생기면 그때는
     * 「진행 중인 약속」을 [readGate] 가 들고 있어야 한다 — 지금의 `loaded` 검사로는 그 회차의 기본
     * 선택과 첫 조회가 조용히 빠진다.
     */
    async loadDefault() {
      await this.loadRoots()
      // **읽었는가**를 묻는다 — 같은 읽기가 이미 나가 있어 문에 막혔을 때도 `roots` 는 비어 있다.
      // 둘을 같은 모양으로 읽으면 기본 선택과 첫 조회가 조용히 빠진다
      if (!this.loaded) return

      const first = this.tree.roots[0]
      if (!first || hasRules(this.rules)) return

      this.rules = withRule(this.rules, { target: RULE_TARGET.CUSTS, id: first.custCd }, true, this.index)
      // 기본 선택은 **사람이 취소할 편집이 아니다** — 확정본이 먼저 서야 첫 `resolve` 가 실패해도
      // 다음에 연 창이 그 기본을 그대로 보여 준다. 그래서 확정도 창과 무관한 출처로 시작한다
      this.committedRules = copyRules(this.rules)
      this.confirm(CONFIRM_ORIGIN.DEFAULT)
    },

    async loadRoots() {
      const ticket = this.gate.start(SCOPE_READ_KEY.roots + 'root')
      // 이미 같은 읽기가 나가 있다 — **읽지 않았다**는 사실을 호출부가 알아야 한다([loaded] 를 본다)
      if (!ticket) return false
      this.syncBusy()
      this.failed = false

      try {
        const roots = custNodesOf(await this.read(SCOPE_CUSTS_API, custsQuery(null)))
        if (!this.gate.isCurrent(ticket)) return

        this.learn(roots, null)
        this.tree = { roots, children: {}, expanded: {} }
        this.loaded = true
        this.$emit('roots', roots)
      } catch (error) {
        if (this.gate.isCurrent(ticket)) this.onReadFailed(error)
      } finally {
        this.gate.end(ticket)
        this.syncBusy()
      }
    },

    /** 사업장 한 곳의 자식 — 사업장과 첫 장 매장을 함께 읽는다(펼침 한 번에 요청 둘) */
    async loadChildren(node) {
      // 읽는 중에 접었다 다시 펴도 요청은 하나다 — 같은 키가 살아 있으면 문이 막는다
      const ticket = this.gate.start(SCOPE_READ_KEY.custChildren + node.key)
      if (!ticket) return
      this.syncBusy()

      try {
        const [custResult, pageResult] = await Promise.all([
          this.read(SCOPE_CUSTS_API, custsQuery(node.custCd)),
          this.read(SCOPE_STORES_API, storesQuery(node.custCd)),
        ])
        if (!this.gate.isCurrent(ticket)) return

        const custs = custNodesOf(custResult)
        const page = storePageOf(pageResult)

        this.learn(custs, node.custCd)
        this.learnStores(page.items, node.custCd)
        this.setChildren(node.key, { custs, stores: page.items, total: page.total, hasMore: page.hasMore, page: 0 })
      } catch (error) {
        if (!this.gate.isCurrent(ticket)) return
        // 그 가지를 **닫는다** — 창 전체를 실패로 덮지 않는다(다른 가지는 이미 읽었다).
        // 뒤집기가 아니다: 읽는 동안 사람이 이미 접었으면 뒤집기는 그 가지를 **다시 펴** 자식 없는
        // 「읽는 중」만 남긴다
        this.tree = Object.assign({}, this.tree, { expanded: withoutKey(this.tree.expanded, node.key) })
        this.onReadFailed(error)
      } finally {
        this.gate.end(ticket)
        this.syncBusy()
      }
    },

    /**
     * 「더 보기」 — 읽은 장에 하나 더한다([nextStorePage]).
     *
     * 트리의 가지와 **검색으로 들어간 사업장** 둘 다 같은 이 경로다: 줄이 들고 온 `parentKey` 가
     * 어느 쪽인지 답하고([pageOf]), 붙이는 자리도 읽기 수명도 그 키가 정한다([storePageReadKey]).
     * 두 벌이면 검색 쪽에만 「더 보기」가 없거나 장 번호가 어긋난다.
     */
    async loadMore(row) {
      const loaded = this.pageOf(row.parentKey)
      if (!loaded) return

      const next = nextStorePage(loaded.page)
      // 두 번 빠르게 눌러도 같은 장을 두 번 받지 않는다 — 받으면 같은 매장이 두 줄이 되고 장이 멈춘다
      const ticket = this.gate.start(storePageReadKey(row.parentKey, next))
      if (!ticket) return
      this.syncBusy()

      try {
        const page = storePageOf(await this.read(SCOPE_STORES_API, storesQuery(row.custCd, next)))
        if (!this.gate.isCurrent(ticket)) return

        this.learnStores(page.items, row.custCd)
        this.setPage(row.parentKey, {
          custs: loaded.custs,
          stores: loaded.stores.concat(page.items),
          total: page.total,
          hasMore: page.hasMore,
          page: next,
        })
      } catch (error) {
        if (this.gate.isCurrent(ticket)) this.onReadFailed(error)
      } finally {
        this.gate.end(ticket)
        this.syncBusy()
      }
    },

    /** 그 키의 매장 장 — 트리의 가지이거나 검색 상세다(둘은 **같은 모양**이라 호출부가 안 갈린다) */
    pageOf(key) {
      if (this.custStores && this.custStores.node.key === key) return this.custStores.loaded

      return this.tree.children[key]
    },

    /**
     * 읽은 장을 제자리에 붙인다 — 트리면 가지에, 검색 상세면 그 상세에.
     *
     * 검색 상세를 이미 떠났으면 **아무 데도 붙이지 않는다**. 그 갈래는 [storePageReadKey] 의 취소가
     * 먼저 막지만, 여기서 `setChildren('X-…')` 로 떨어지면 트리에 실재하지 않는 키가 남는다.
     */
    setPage(key, loaded) {
      if (this.custStores && this.custStores.node.key === key) {
        this.custStores = { node: this.custStores.node, loaded }

        return
      }
      if (String(key).indexOf(SEARCH_NODE_PREFIX) === 0) return

      this.setChildren(key, loaded)
    },

    /**
     * 검색한 사업장의 매장 — 같은 조회를 쓰고, 상태도 트리의 가지와 **같은 모양**으로 든다.
     *
     * 첫 장도 「더 보기」와 **같은 키 규칙**을 쓴다([storePageReadKey]) — 그래야 상세를 떠나는 한 번의
     * `cancel(SCOPE_READ_KEY.searchStores)` 가 둘 다 덮는다.
     */
    async openCustStores(node) {
      const key = searchNodeKey(node.custCd)
      const ticket = this.gate.start(storePageReadKey(key, 0))
      if (!ticket) return
      this.syncBusy()

      try {
        const page = storePageOf(await this.read(SCOPE_STORES_API, storesQuery(node.custCd)))
        if (!this.gate.isCurrent(ticket)) return

        this.learnStores(page.items, node.custCd)
        this.custStores = {
          node: { key, custCd: node.custCd, custNm: node.custNm, directStoreCount: node.directStoreCount },
          loaded: { custs: [], stores: page.items, total: page.total, hasMore: page.hasMore, page: 0 },
        }
        this.activeIndex = -1
      } catch (error) {
        if (this.gate.isCurrent(ticket)) this.onReadFailed(error)
      } finally {
        this.gate.end(ticket)
        this.syncBusy()
      }
    },

    backToSearch() {
      // 나가 있던 사업장 매장 조회는 **첫 장·다음 장 모두** 버린다 — 늦게 오면 돌아온 검색을 덮는다
      this.gate.cancel(SCOPE_READ_KEY.searchStores)
      this.syncBusy()
      this.custStores = null
      this.activeIndex = -1
    },

    /**
     * **읽기 수명주기의 주인** — 검색 의도가 바뀌는 자리는 여기 하나다.
     *
     * 보낼 수 있는 질의어인지와 **무관하게 먼저** 나가 있던 탐색(검색·사업장 매장)을 버린다. 갈래
     * 안쪽에 취소를 두었을 때 두 가지가 샜다: ① A→B→A 로 되돌리면 세 번째 A 가 살아 있는 첫 A 티켓
     * 때문에 나가지 못하고 최신 순번은 B 에 남아 **입력은 A 인데 화면은 B** 가 된다. ② 검색어를 바꿔도
     * `custStores:` 는 살아 있어 늦게 온 사업장 매장 목록이 새 검색 결과를 덮는다.
     *
     * 사업장 **펼침**(`cust:`·`stores:`)은 건드리지 않는다 — 트리를 펴는 일은 검색과 독립이다.
     * 검색 상세의 「더 보기」는 `custStores:` 앞머리 아래라 이 한 줄이 함께 덮는다([storePageReadKey]).
     */
    onQueryInput() {
      this.clearDebounce()
      this.gate.cancel(SCOPE_READ_KEY.search)
      this.gate.cancel(SCOPE_READ_KEY.searchStores)
      this.guard.invalidate()
      this.syncBusy()
      this.custStores = null

      if (!isSearchable(this.query)) {
        this.searching = false
        this.searchRows = []

        return
      }

      this.debounce = setTimeout(this.runSearch, SEARCH_DEBOUNCE_MS)
    },

    /** 검색은 **서버가 한다**(§1-5) — 범위 안에서만 찾고, 경로를 함께 준다 */
    async runSearch() {
      const ticket = this.gate.start(SCOPE_READ_KEY.search + this.query.trim())
      if (!ticket) return
      const seq = this.guard.begin()

      this.searching = true
      this.syncBusy()

      try {
        const result = searchResultOf(await this.read(SCOPE_SEARCH_API, searchQuery(this.query)))
        if (!this.guard.isLatest(seq) || !this.gate.isCurrent(ticket)) return

        result.custs.forEach((node) => this.learnPath(node))
        result.stores.forEach((store) => this.learnPath(store))

        // 매장이 먼저다 — 고를 수 있는 줄을 위에 둔다. 사업장은 「매장 보기」로 들어가는 길이다
        this.searchRows = result.stores
          .map((row) => Object.assign({}, row, { depth: 0 }))
          .concat(result.custs.map((row) => Object.assign({}, row, { depth: 0 })))
        this.activeIndex = -1
      } catch (error) {
        if (this.guard.isLatest(seq) && this.gate.isCurrent(ticket)) {
          this.searchRows = []
          this.onReadFailed(error)
        }
      } finally {
        this.gate.end(ticket)
        this.syncBusy()
      }
    },

    // ── 사용자 입력 ────────────────────────────────────────────────────────

    /**
     * 줄을 **연다** — 마우스와 키보드가 같은 답을 쓰는 두 줄이다. 처리했으면 `true`.
     *
     * 「더 보기」와 검색 결과의 사업장 진입이 클릭에만 있던 동안 키보드로는 51번째 매장에 닿을
     * 길이 없었다. 검색 결과의 사업장은 `path` 를 갖는 것으로 트리의 사업장과 갈린다 — 펼 가지가
     * 아니라 들어갈 곳이다.
     *
     * **트리의 사업장은 여기 없다.** 한때 그것까지 가져갔는데, 다중 모드의 커서가 가장 흔히 서 있는
     * 줄이 뿌리 사업장이라 Enter 가 확정이 아니라 펼침이 됐다 — 바닥글의 「Enter 확정」이 거짓이 되고
     * 키보드로 확정할 길이 사라진다. 펼침은 이미 →·←와 클릭이 갖고 있어 Enter 가 한 번 더 할 이유가 없다.
     */
    activateRow(row) {
      if (!row) return false
      if (row.type === NODE_TYPE.MORE) {
        this.loadMore(row)

        return true
      }
      if (row.type === NODE_TYPE.CUST && row.path) {
        this.openCustStores(row)

        return true
      }

      return false
    },

    onRowClick(row, index) {
      this.activeIndex = index
      if (this.activateRow(row)) return
      // 트리의 사업장은 클릭이 편다 — 종전 그대로다(검색 결과 사업장은 위에서 갈렸다)
      if (row.type === NODE_TYPE.CUST) return this.toggle(row)
      if (this.multiple) {
        if (isPickable(row) || row.type === NODE_TYPE.DIRECT) this.check(row, this.checkState(row) !== SELECTION.ON)

        return
      }
      if (isPickable(row)) this.pick(row)
    },

    toggle(node) {
      if (node.type !== NODE_TYPE.CUST) return
      const expanded = toggleExpanded(this.tree.expanded, node.key)

      this.tree = Object.assign({}, this.tree, { expanded })
      if (expanded[node.key] && !this.tree.children[node.key]) this.loadChildren(node)
    },

    /** 체크 한 번 = 규칙 하나 — 접으로 풀지 않고 [withRule] 이 최소 집합으로 유지한다 */
    check(row, next) {
      const pick = this.pickOf(row)
      if (!pick) return

      this.rules = withRule(this.rules, pick, next === true, this.index)
    },

    checkState(row) {
      if (row.type === NODE_TYPE.CUST)
        return custSelection(this.rules, row.custCd, this.chainOfCust(row.custCd), this.index)
      if (row.type === NODE_TYPE.DIRECT)
        return directSelection(this.rules, row.custCd, this.chainOfCust(row.custCd), this.index)
      if (row.type !== NODE_TYPE.STORE) return SELECTION.OFF

      const custCd = this.index.storeCust[row.storeCd]

      return storeSelected(this.rules, row.storeCd, this.chainOfCust(custCd)) ? SELECTION.ON : SELECTION.OFF
    },

    /**
     * 그 줄이 만드는 **규칙의 대상** — 계보는 싣지 않는다([withRule] 이 색인에서 유도한다).
     *
     * 갈래를 셋 다 명시하고 `null` 로 끝낸다: 마지막을 「나머지는 매장」으로 두면 [hasCheckAxis] 에
     * 네 번째 타입이 느는 날 그 줄이 `id: undefined` 인 매장 규칙이 되어 조용히 아무 일도 안 한다.
     */
    pickOf(row) {
      if (!hasCheckAxis(row)) return null
      if (row.type === NODE_TYPE.CUST) return { target: RULE_TARGET.CUSTS, id: row.custCd }
      if (row.type === NODE_TYPE.DIRECT) return { target: RULE_TARGET.DIRECT_OF, id: row.custCd }
      if (row.type === NODE_TYPE.STORE) return { target: RULE_TARGET.STORES, id: row.storeCd }

      return null
    },

    removeChip(chip) {
      this.rules = withoutRule(this.rules, chip.side, chip.target, chip.id, this.index)
    },

    clearRules() {
      this.rules = emptyRules()
    },

    chipLabel(chip) {
      const count = chip.count == null ? '' : ` ${chip.exclude ? '−' : ''}${chip.count}`

      return `${chip.label}${this.targetSuffix(chip.target)}${count}`
    },

    targetSuffix(target) {
      if (target === RULE_TARGET.CUSTS) return ` ${this.text.chipAll}`
      if (target === RULE_TARGET.DIRECT_OF) return ` ${this.text.chipDirect}`

      return ''
    },

    pick(row) {
      this.$emit('pick', row)
      this.$emit('update:visible', false)
    },

    /**
     * 확정 — 규칙을 **서버가** 매장 집합으로 푼다(화면이 나열을 지어내지 않는다).
     *
     * 응답을 **요청 당시의 선택과 창의 수명에 묶는다**. 종전에는 규칙만 스냅샷이고 칩은 응답 시점의
     * 현재 값이라, 확정 중에 키보드로 다른 곳을 고르면 **A 의 매장 집합 + B 의 칩**이 함께 나갔다.
     * 취소·중복 Enter 도 막히지 않아 Esc 로 닫은 뒤 도착한 응답이 부모의 조회 조건을 바꿨다.
     *
     * @param {string} [origin] 어디서 시작했나([CONFIRM_ORIGIN]) — 기본은 창 안이고,
     *   화면 진입의 기본 선택만 `default` 로 부른다(창을 닫아도 살아야 한다)
     */
    async confirm(origin) {
      // **중복 가드가 보는 것은 창 안의 확정뿐이다**([resolving] 이 같은 축을 본다). 두 번째 확정이
      // 첫 번째를 무효로 만들면 사람이 누른 확정이 조용히 사라지므로 창 안에서는 막는다. 진행 중인
      // 것이 **진입 기본 확정**이면 이야기가 다르다: 사람이 창을 열어 고르고 누른 확정은 그 기본을
      // **대체해야** 한다. 막으면 그 확정이 삼켜지고, 뒤에 도착한 기본 확정이 열린 창을 닫으며
      // 편집을 버린다.
      if (this.resolving) return

      const rules = copyRules(this.rules)
      const chips = this.chips.slice()
      const seq = this.confirmGuard.begin()

      const from = origin || CONFIRM_ORIGIN.DIALOG

      if (!hasRules(rules)) return this.commitConfirm(rules, { storeCds: [], total: 0, chips: [], count: 0 }, from)

      this.confirmInFlight = { seq, origin: from }

      try {
        const response = await this.$axios.post(SCOPE_RESOLVE_API, resolveBody(rules), SILENT_READ_CONFIG)
        if (!this.confirmGuard.isLatest(seq)) return
        const result = resolveResultOf(response.data)
        const local = selectedCount(rules, this.index)

        // 로컬 합계와 서버가 갈리면 **서버가 정본**이다 — 화면은 그 사실만 남긴다
        if (local != null && local !== result.total) {
          console.warn('store-picker: 로컬 합계와 resolve 합계가 다르다', local, result.total)
        }

        this.commitConfirm(
          rules,
          {
            storeCds: result.storeCds,
            total: result.total,
            chips,
            count: result.total,
          },
          from
        )
      } catch (error) {
        if (!this.confirmGuard.isLatest(seq)) return
        if (httpStatusOf(error) === 400) {
          this.$message({ type: 'warning', message: this.text.tooManyRules })

          return
        }
        this.onReadFailed(error)
      } finally {
        // **자기 순번일 때만** 내려놓는다 — 옛 요청의 `finally` 가 새 확정의 로딩을 풀면 안 된다
        if (this.confirmInFlight && this.confirmInFlight.seq === seq) this.confirmInFlight = null
      }
    },

    /**
     * 확정본을 세우고 부모에게 준다 — **emit 뒤에** 닫으므로 이 확정이 닫힘에 버려지지 않는다.
     *
     * **창을 닫는 것도 창 안에서 시작한 확정만 한다**([CONFIRM_ORIGIN]) — 닫힘 워처가 쓰는 그 규칙과
     * 같은 한 문장이다. 진입 기본 확정이 늦게 도착했을 때 이 줄이 출처를 보지 않으면, 그 왕복 동안
     * 사람이 열어 둔 창을 닫고 **아직 확정하지 않은 편집본을 버린다**(값은 기본값으로 나가므로 틀리지
     * 않지만, 고르던 것이 사라진다).
     */
    commitConfirm(rules, payload, origin) {
      this.committedRules = rules
      this.$emit('confirm', payload)
      if (origin !== CONFIRM_ORIGIN.DEFAULT) this.$emit('update:visible', false)
    },

    // ── 의미(스크린리더가 읽는 것) ─────────────────────────────────────────

    /**
     * 줄의 DOM id — **창 인스턴스마다 다르다**. 한 화면이 칸을 여럿 두고(검색 칸 + 다이얼로그 칸) 창이
     * 전부 `append-to-body` 라, 줄 키만으로 id 를 만들면 `body` 에 같은 id 가 둘 생긴다.
     * `aria-activedescendant` 는 document 에서 id 를 푸는 속성이라 **남의 창 줄**을 가리키게 된다.
     */
    rowDomId(row) {
      return 'store-picker-' + this._uid + '-row-' + row.key
    },

    /** 펼침은 **사업장만** 갖는 상태다 — 매장·묶음 행에는 이 속성이 아예 없어야 한다(undefined) */
    ariaExpanded(row) {
      if (row.type !== NODE_TYPE.CUST || !hasChildren(row)) return undefined

      return this.tree.expanded[row.key] ? 'true' : 'false'
    },

    /** 체크 상태는 다중 모드에만 있다 — 세 상태를 그대로 말한다(부분 선택은 mixed) */
    ariaChecked(row) {
      // 체크 축을 갖는 세 줄만 상태를 갖는다([hasCheckAxis]) — 「볼 수 있는 매장이 없습니다」 같은
      // 안내 줄에 붙이면 스크린리더가 문장을 체크박스로 읽는다
      if (!this.multiple || !hasCheckAxis(row)) return undefined
      const state = this.checkState(row)

      return state === SELECTION.PARTIAL ? 'mixed' : String(state === SELECTION.ON)
    },

    /** 단일 모드의 「지금 고를 줄」 — 고를 수 없는 줄은 선택 상태를 갖지 않는다 */
    ariaSelected(row) {
      if (this.multiple || !isPickable(row)) return undefined

      return this.rows[this.activeIndex] === row ? 'true' : 'false'
    },

    // ── 키보드 ─────────────────────────────────────────────────────────────

    moveBy(delta) {
      this.activeIndex = moveCursor(this.rows, this.activeIndex, delta)
    },

    /**
     * →·← 로 가지를 편다 — **트리의 사업장만**이다.
     *
     * 검색 결과의 사업장은 트리 노드와 **같은 키**를 가지므로, 그 줄에서 → 를 누르면 보이지도 않는
     * 트리 가지가 펴지고 자식 조회까지 나간다(검색 중에는 `rows` 가 검색 결과다). 그 줄에서 Enter 는
     * 이미 「들어가기」로 갈렸으므로([activateRow]) 두 축이 같은 대상을 가리키게 둔다.
     */
    expandActive(open) {
      const row = this.rows[this.activeIndex]
      if (!row || row.type !== NODE_TYPE.CUST || row.path) return
      if (!!this.tree.expanded[row.key] === open) return

      this.toggle(row)
    },

    /**
     * Space = 활성 행 체크. **목록에 포커스가 있을 때만**이다 — 검색 칸에서 누른 띄어쓰기를 가로채면
     * 「교촌 치킨」 같은 상호를 아예 찾을 수 없다(이 핸들러가 목록에만 달린 이유).
     */
    onSpace(event) {
      if (!this.multiple) return
      const row = this.rows[this.activeIndex]
      if (!row) return

      event.preventDefault()
      this.check(row, this.checkState(row) !== SELECTION.ON)
    },

    /**
     * Enter — **여는 줄이 먼저 답하고**([activateRow]) 나머지는 종전대로 「다중 → 확정 / 단일 → pick」이다.
     *
     * 여는 줄은 둘뿐이다(「더 보기」·검색 결과 사업장). 그 둘에서 Enter 가 곧바로 확정이던 동안
     * 키보드만 쓰는 사람은 창이 닫히는 것만 보았고, 반대로 트리의 사업장까지 여기로 가져갔을 때는
     * 바닥글의 「Enter 확정」이 거짓이 됐다 — 이 메서드가 참이 되게 하는 것이 그 문구의 계약이다.
     */
    onEnter() {
      const row = this.rows[this.activeIndex]
      if (this.activateRow(row)) return
      if (this.multiple) return this.confirm()
      if (isPickable(row)) this.pick(row)
    },

    // ── 조회 경계 ──────────────────────────────────────────────────────────

    /** 로딩 마스크의 근거는 **문**이다 — 자리마다 켜고 끄지 않는다(버려진 읽기는 바쁨이 아니다) */
    syncBusy() {
      this.busy = this.gate.busy()
    },

    async read(url, params) {
      const response = await this.$axios.get(url, Object.assign({ params }, SILENT_READ_CONFIG))

      return response.data
    },

    /**
     * 실패 한 곳 — **「이 서버는 트리를 모른다」와 「아픈 서버」를 가른다**([scopeUnavailable]).
     * 앞은 부모가 옛 셀렉트·옛 트리로 떨어질 신호이고, 뒤는 이 창이 「다시 시도」로 답할 실패다.
     */
    onReadFailed(error) {
      if (scopeUnavailable({ status: httpStatusOf(error) })) {
        this.$emit('unavailable')
        this.$emit('update:visible', false)

        return
      }

      this.failed = true
    },

    retry() {
      this.failed = false
      this.loaded = false
      this.loadRoots()
    },

    setChildren(key, loaded) {
      this.tree = Object.assign({}, this.tree, {
        children: Object.assign({}, this.tree.children, { [key]: loaded }),
      })
    },

    /** 불러온 노드를 계보 색인에 적는다 — 규칙 판정·3상태·수가 이것만 읽는다 */
    learn(nodes, parentCustCd) {
      const index = this.nextIndex()

      nodes.forEach((node) => {
        if (parentCustCd != null) index.custParents[node.custCd] = parentCustCd
        index.names[node.custCd] = node.custNm
        index.counts[node.custCd] = { direct: node.directStoreCount, subtree: node.subtreeStoreCount }
      })
      this.index = index
    },

    learnStores(stores, custCd) {
      const index = this.nextIndex()

      stores.forEach((store) => {
        // 행이 말하는 사업장이 먼저다 — 요청한 사업장은 그것이 없을 때의 폴백이다(검색 결과에는 없다)
        index.storeCust[store.storeCd] = store.custCd != null ? store.custCd : custCd
      })
      // 이름 사전은 칸과 **같은 한 함수**가 만든다 — 두 모드가 같은 사전을 쓴다
      index.storeNames = Object.assign(index.storeNames, storeNamesOf(stores))
      this.index = index
    },

    /** 검색 결과의 경로도 계보다 — 그 길로 고른 규칙이 3상태를 옳게 낸다 */
    learnPath(row) {
      const index = this.nextIndex()
      const path = (row.path || []).filter((step) => step.custCd != null)

      path.forEach((step, position) => {
        index.names[step.custCd] = step.custNm
        if (position > 0) index.custParents[step.custCd] = path[position - 1].custCd
      })

      // **축으로 가른다** — 매장 행도 자기 `custCd`(어느 사업장 아래인가)를 갖는다. 그것을 사업장 행과
      // 같이 다루면 이름·매장 수 칸을 `undefined` 로 덮어써, 트리로 돌아왔을 때 그 사업장이 이름을 잃는다
      if (row.type === NODE_TYPE.CUST && row.custCd != null) {
        index.names[row.custCd] = row.custNm
        index.counts[row.custCd] = { direct: row.directStoreCount, subtree: row.subtreeStoreCount }
        if (path.length > 0) index.custParents[row.custCd] = path[path.length - 1].custCd
      }
      if (row.storeCd != null) {
        // 행이 말하는 사업장이 먼저이고, 경로의 끝은 그것이 없을 때의 폴백이다
        const parent = row.custCd != null ? row.custCd : path.length > 0 ? path[path.length - 1].custCd : null
        if (parent != null) index.storeCust[row.storeCd] = parent
        index.storeNames[row.storeCd] = row.storeNm
      }
      this.index = index
    },

    nextIndex() {
      return {
        custParents: Object.assign({}, this.index.custParents),
        storeCust: Object.assign({}, this.index.storeCust),
        counts: Object.assign({}, this.index.counts),
        names: Object.assign({}, this.index.names),
        storeNames: Object.assign({}, this.index.storeNames),
      }
    },

    /** 그 사업장부터 뿌리까지 — 순서 규칙의 주인은 [chainOf] 다(정규화도 같은 것을 쓴다) */
    chainOfCust(custCd) {
      return chainOf(this.index, custCd)
    },

    clearDebounce() {
      if (this.debounce) clearTimeout(this.debounce)
      this.debounce = null
    },

    // ── 그리기(판정은 전부 순수 함수) ──────────────────────────────────────

    rowClass(row, index) {
      return {
        'is-active': index === this.activeIndex,
        'is-pickable': this.multiple ? row.type !== NODE_TYPE.LOADING : isPickable(row),
        'is-inactive': isInactive(row) && row.type === NODE_TYPE.CUST,
        'is-note': row.type === NODE_TYPE.LOADING || row.type === NODE_TYPE.EMPTY,
      }
    },
    caretClass(row) {
      if (!row.expandable) return 'store-picker__caret--none'

      return row.expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'
    },
    inactive: isInactive,
    shopGroup: isShopGroup,
    countText: storeCountText,
    subText: storeSubText,
    pathLine(row) {
      const path = pathText(row.path)

      return path ? `${STORE_PICKER_TEXT.pathLabel} ${path}` : ''
    },
  },
}
</script>

<style scoped>
.store-picker__search {
  margin-bottom: 8px;
}
.store-picker__hint,
.store-picker__empty {
  color: #909399;
  font-size: 12px;
  margin: 6px 0 0;
}
.store-picker__crumb {
  align-items: center;
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.store-picker__crumb-name {
  font-size: 13px;
  font-weight: 600;
}
.store-picker__recent {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.store-picker__section {
  color: #909399;
  font-size: 12px;
}
.store-picker__rows {
  border: 1px solid var(--color-border, #ebeef5);
  border-radius: 4px;
  max-height: 380px;
  min-height: 120px;
  overflow-y: auto;
}
.store-picker__row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  line-height: 1.6;
  padding: 4px 8px;
}
.store-picker__row.is-pickable {
  cursor: pointer;
}
/* 마우스가 지나가는 것은 **강조**이지 커서 이동이 아니다 — 키보드로 세워 둔 자리를 빼앗지 않는다 */
.store-picker__row.is-pickable:hover {
  background-color: #f5f7fa;
}
/* 목록 자체가 포커스를 갖는다(roving focus) — 어디에 있는지는 활성 줄이 말한다 */
.store-picker__rows:focus {
  outline: none;
}
.store-picker__rows:focus-visible {
  outline: 2px solid var(--color-primary, #409eff);
  outline-offset: 2px;
}
.store-picker__row.is-active {
  background-color: var(--color-primary-light, #ecf5ff);
}
/** 흐린 것은 **폐업 사업장 노드**뿐이다 — 매장은 활성만 내려온다(§1-5 매장 기준) */
.store-picker__row.is-inactive {
  opacity: 0.55;
}
.store-picker__row.is-note {
  color: #909399;
  font-size: 12px;
}
.store-picker__caret {
  color: #909399;
  width: 12px;
}
.store-picker__caret--none {
  visibility: hidden;
}
.store-picker__name {
  word-break: keep-all;
}
.store-picker__direct {
  color: #606266;
  font-size: 12px;
}
.store-picker__sub {
  color: #909399;
  font-size: 12px;
  word-break: keep-all;
}
.store-picker__more {
  color: var(--color-primary, #409eff);
  cursor: pointer;
  font-size: 12px;
}
.store-picker__summary {
  align-items: center;
  border-top: 1px solid var(--color-border, #ebeef5);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
}
.store-picker__keys {
  color: #909399;
  float: left;
  font-size: 12px;
  line-height: 32px;
}
</style>
